import styled from 'styled-components';
import {graphql} from 'gatsby';
import React, {FC} from 'react';

import {NavigationCallToActionLarge} from '../../components/modules/navigation/callToAction/navigationCallToActionLarge';
import {NavigationCTASignup} from '../../components/modules/navigation/callToAction/navigationCallToActionSignup';
import {NavigationCallToActionSmall} from '../../components/modules/navigation/callToAction/navigationCallToActionSmall';
import {NavigationCallToActionWithEmailR4} from '../../components/modules/navigation/callToAction/navigationCallToActionWithEmailR4';
import {NavigationCtaFragment} from '../../lib/queryTypes';
import {
  DynamicNavigationCallToActionTwoButtonProps,
  NavigationCallToActionTwoButton
} from '../../components/modules/navigation/callToAction/navigationCallToActionTwoButtons';
import {NavigationCallToActionTwoButtonR4Classroom} from '../../components/modules/navigation/callToAction/navigationCallToActionTwoButtonsR4Classroom';
import {RichText, RichTextBlockType} from './richText';
import {
  NavigationCallToActionR4FrontPage,
  NavigationCallToActionR4FrontPageProps
} from '../../components/modules/navigation/callToAction/navigationCallToActionR4FrontPage';

const StyledP = styled.p`
  margin: 0;
`;

export const NavigationCTA: FC<DynamicNavigationCallToActionTwoButtonProps &
  NavigationCtaFragment & {onSubmit?: NavigationCallToActionR4FrontPageProps['onSubmit']}> = props => {
    const bullets = props.descriptionBullets
      ? props.descriptionBullets.map(bullet => (typeof bullet === 'string' ? bullet : '')).filter(Boolean)
      : [];

    switch (props.ctaType) {
      case 'SmallWithLink':
        return (
          <NavigationCallToActionSmall
          // @ts-ignore
            color={props.ctaColor! && props.ctaColor!.toLowerCase()}
            title={
              props.title && (
                <RichText
                  document={props.title!.json}
                  blockOverrides={{
                    [RichTextBlockType.PARAGRAPH]: (node: any, children: any) => <StyledP>{children}</StyledP>
                  }}
                />
              )
            }
            description={props.description!}
            href={props.ctaLink!}
          />
        );
      case 'Large':
        return (
          <NavigationCallToActionLarge
          // @ts-ignore
            color={props.ctaColor! && props.ctaColor!.toLowerCase()}
            title={
              props.title && (
                <RichText
                  document={props.title!.json}
                  blockOverrides={{
                    [RichTextBlockType.PARAGRAPH]: (node: any, children: any) => <StyledP>{children}</StyledP>
                  }}
                />
              )
            }
            bullets={bullets}
            buttonLabel={props.primaryButtonLabel!}
            inputPlaceholder={props.emailInputPlaceholder!}
            primaryLabel={props.primaryButtonLabel!}
            primaryHref={props.primaryButtonLink!}
            secondaryHref={props.secondaryButtonLink!}
            secondaryLabel={props.secondaryButtonLabel!}
            tertiaryHref={props.tertiaryLink!}
            tertiaryLabel={props.tertiaryLinkLabel!}
          />
        );
      case 'MediumWithEmail':
        if (props.designVersion === 'r4')
          return (
            <NavigationCallToActionWithEmailR4
              description={props.description!}
              title={
                props.title && (
                  <RichText
                    document={props.title!.json}
                    blockOverrides={{
                      [RichTextBlockType.PARAGRAPH]: (node: any, children: any) => <StyledP>{children}</StyledP>
                    }}
                  />
                )
              }
              buttonLabel={props.primaryButtonLabel!}
              inputPlaceholder={props.emailInputPlaceholder!}
              submitUrl={props.submitUrl!}
            />
          );
        if (props.designVersion === 'r4-frontPage')
          return (
            <NavigationCallToActionR4FrontPage
              title={
                props.title && (
                  <RichText
                    document={props.title!.json}
                    blockOverrides={{
                      [RichTextBlockType.PARAGRAPH]: (node: any, children: any) => <StyledP>{children}</StyledP>
                    }}
                  />
                )
              }
              onSubmit={props.onSubmit!}
              buttonLabel={props.primaryButtonLabel!}
              inputPlaceholder={props.emailInputPlaceholder!}
            />
          );

        return (
          <NavigationCTASignup
          // @ts-ignore
            color={props.ctaColor! && props.ctaColor!.toLowerCase()}
            title={
              props.title && (
                <RichText
                  document={props.title!.json}
                  blockOverrides={{
                    [RichTextBlockType.PARAGRAPH]: (node: any, children: any) => <StyledP>{children}</StyledP>
                  }}
                />
              )
            }
            submitUrl={props.submitUrl!}
            buttonLabel={props.primaryButtonLabel!}
            inputPlaceholder={props.emailInputPlaceholder!}
          />
        );
      case 'MediumWithButtons':
        if (props.designVersion === 'r4-classroom')
          return (
            <NavigationCallToActionTwoButtonR4Classroom
            // @ts-ignore
              title={
                props.title && (
                  <RichText
                    document={props.title!.json}
                    blockOverrides={{
                      [RichTextBlockType.PARAGRAPH]: (node: any, children: any) => <StyledP>{children}</StyledP>
                    }}
                  />
                )
              }
              primaryLabel={props.primaryButtonLabel!}
              primaryHref={props.primaryButtonLink!}
              primaryHover={props.ctaColor ? props.ctaColor.toLowerCase() === 'blue' : false}
              secondaryHref={props.secondaryButtonLink!}
              secondaryLabel={props.secondaryButtonLabel!}
              onButtonClick={props.onButtonClick!}
            />
          );

        return (
          <NavigationCallToActionTwoButton
          // @ts-ignore
            color={props.ctaColor! && props.ctaColor!.toLowerCase()}
            title={
              props.title && (
                <RichText
                  document={props.title!.json}
                  blockOverrides={{
                    [RichTextBlockType.PARAGRAPH]: (node: any, children: any) => <StyledP>{children}</StyledP>
                  }}
                />
              )
            }
            primaryLabel={props.primaryButtonLabel!}
            primaryHref={props.primaryButtonLink!}
            primaryHover={props.ctaColor ? props.ctaColor.toLowerCase() === 'blue' : false}
            secondaryHref={props.secondaryButtonLink!}
            secondaryLabel={props.secondaryButtonLabel!}
            onButtonClick={props.onButtonClick!}
          />
        );
      default:
        return null;
    }
  };

export const query = graphql`
  fragment NavigationCTA on ContentfulModulesNavigationCta {
    title {
      json
    }
    description
    descriptionBullets
    ctaType
    ctaColor
    ctaLink
    emailInputPlaceholder
    primaryButtonLabel
    primaryButtonLink
    secondaryButtonLabel
    secondaryButtonLink
    submitUrl
    tertiaryLink
    tertiaryLinkLabel
    designVersion
  }
`;
